/**
 * Product Tabs Component
 * Handles tab switching and FAQ accordion functionality
 */

class ProductTabs extends HTMLElement {
  constructor() {
    super();
    this.tabs = [];
    this.panels = [];
    this.faqItems = [];
  }

  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    this.faqItems = Array.from(this.querySelectorAll('[data-faq-item]'));
    
    this.bindTabEvents();
    this.bindFaqEvents();
  }

  /**
   * Bind click and keyboard events to tabs
   */
  bindTabEvents() {
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.handleTabClick.bind(this));
      tab.addEventListener('keydown', this.handleTabKeydown.bind(this));
    });
  }

  /**
   * Bind events to FAQ accordion items
   */
  bindFaqEvents() {
    this.faqItems.forEach((item) => {
      const question = item.querySelector('.product-tabs__faq-question');
      if (question) {
        question.addEventListener('click', () => this.toggleFaq(item));
      }
    });
  }

  /**
   * Handle tab click event
   * @param {Event} event 
   */
  handleTabClick(event) {
    const clickedTab = event.currentTarget;
    this.activateTab(clickedTab);
  }

  /**
   * Handle keyboard navigation for tabs
   * @param {KeyboardEvent} event 
   */
  handleTabKeydown(event) {
    const currentIndex = this.tabs.indexOf(event.currentTarget);
    let newIndex;

    switch (event.key) {
      case 'ArrowLeft':
        newIndex = currentIndex - 1;
        if (newIndex < 0) newIndex = this.tabs.length - 1;
        break;
      case 'ArrowRight':
        newIndex = currentIndex + 1;
        if (newIndex >= this.tabs.length) newIndex = 0;
        break;
      case 'Home':
        newIndex = 0;
        break;
      case 'End':
        newIndex = this.tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.activateTab(this.tabs[newIndex]);
    this.tabs[newIndex].focus();
  }

  /**
   * Activate a specific tab and show its panel
   * @param {HTMLElement} tab 
   */
  activateTab(tab) {
    const tabId = tab.dataset.tab;
    
    // Deactivate all tabs
    this.tabs.forEach((t) => {
      t.classList.remove('product-tabs__tab--active');
      t.setAttribute('aria-selected', 'false');
      t.setAttribute('tabindex', '-1');
    });

    // Hide all panels
    this.panels.forEach((panel) => {
      panel.classList.remove('product-tabs__panel--active');
      panel.setAttribute('hidden', '');
    });

    // Activate clicked tab
    tab.classList.add('product-tabs__tab--active');
    tab.setAttribute('aria-selected', 'true');
    tab.removeAttribute('tabindex');

    // Show corresponding panel
    const panel = this.querySelector(`[data-panel="${tabId}"]`);
    if (panel) {
      panel.classList.add('product-tabs__panel--active');
      panel.removeAttribute('hidden');
    }

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('tab-changed', {
      detail: { tab: tabId },
      bubbles: true
    }));
  }

  /**
   * Toggle FAQ accordion item
   * @param {HTMLElement} item 
   */
  toggleFaq(item) {
    const question = item.querySelector('.product-tabs__faq-question');
    const answer = item.querySelector('.product-tabs__faq-answer');
    
    if (!question || !answer) return;

    const isExpanded = question.getAttribute('aria-expanded') === 'true';
    
    // Close all other FAQ items (optional: remove this for multi-open)
    this.faqItems.forEach((otherItem) => {
      if (otherItem !== item) {
        const otherQuestion = otherItem.querySelector('.product-tabs__faq-question');
        const otherAnswer = otherItem.querySelector('.product-tabs__faq-answer');
        if (otherQuestion && otherAnswer) {
          otherQuestion.setAttribute('aria-expanded', 'false');
          otherAnswer.setAttribute('hidden', '');
        }
      }
    });

    // Toggle current item
    if (isExpanded) {
      question.setAttribute('aria-expanded', 'false');
      answer.setAttribute('hidden', '');
    } else {
      question.setAttribute('aria-expanded', 'true');
      answer.removeAttribute('hidden');
    }
  }

  /**
   * Programmatically switch to a specific tab
   * @param {string} tabId 
   */
  switchToTab(tabId) {
    const tab = this.querySelector(`[data-tab="${tabId}"]`);
    if (tab) {
      this.activateTab(tab);
    }
  }
}

// Register custom element
if (!customElements.get('product-tabs')) {
  customElements.define('product-tabs', ProductTabs);
}

// Export for module usage
export { ProductTabs };

